import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const roleLabels = {
  admin: 'Administratör',
  contentCreator: 'Redaktör',
  sekreterare: 'Sekreterare',
  readOnly: 'Läsbehörig'
};

const roleOptions = [
  { value: 'admin', label: 'Administratör' },
  { value: 'contentCreator', label: 'Redaktör' },
  { value: 'sekreterare', label: 'Sekreterare' },
  { value: 'readOnly', label: 'Läsbehörig' }
];

export default function UsersManager() {
  const { user: currentUser, hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  
  // Create user form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('contentCreator');
  const [isCreating, setIsCreating] = useState(false);
  
  // Edit user modal state
  const [editingUser, setEditingUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Password change modal state
  const [passwordUser, setPasswordUser] = useState(null);
  const [newUserPassword, setNewUserPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await AdminAPI.getUsers();
        setUsers(data.users || []);
      } catch (err) {
        showToast('error', 'Kunde inte ladda användare');
        console.error('Failed to load users:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await AdminAPI.getUsers();
      setUsers(data.users || []);
    } catch (err) {
      showToast('error', 'Kunde inte ladda användare');
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (password) => {
    if (password.length < 8) return 'Lösenordet måste vara minst 8 tecken';
    if (!/[A-Z]/.test(password)) return 'Lösenordet måste innehålla minst en stor bokstav';
    if (!/[a-z]/.test(password)) return 'Lösenordet måste innehålla minst en liten bokstav';
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) return 'Lösenordet måste innehålla minst ett specialtecken';
    return null;
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUsername || !newPassword) {
      showToast('error', 'Användarnamn och lösenord krävs');
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      showToast('error', validationError);
      return;
    }

    try {
      setIsCreating(true);
      await AdminAPI.createUser({
        username: newUsername,
        password: newPassword,
        role: newRole
      });
      
      setNewUsername('');
      setNewPassword('');
      setNewRole('contentCreator');
      setShowCreateForm(false);
      
      await loadUsers();
      showToast('success', 'Användare skapad!');
    } catch (err) {
      if (err.message?.includes('already exists') || err.message?.includes('Conflict')) {
        showToast('error', 'Användarnamnet finns redan');
      } else {
        showToast('error', err.message || 'Kunde inte skapa användare');
      }
      console.error('Failed to create user:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    if (!editingUser) return;

    try {
      setIsSaving(true);
      await AdminAPI.updateUser(editingUser.id, { role: editRole });
      
      setEditingUser(null);
      setEditRole('');
      
      await loadUsers();
      showToast('success', 'Användare uppdaterad!');
    } catch (err) {
      showToast('error', err.message || 'Kunde inte uppdatera användare');
      console.error('Failed to update user:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordUser) return;
    
    const validationError = validatePassword(newUserPassword);
    if (validationError) {
      showToast('error', validationError);
      return;
    }

    if (newUserPassword !== confirmPassword) {
      showToast('error', 'Lösenorden matchar inte');
      return;
    }

    try {
      setIsChangingPassword(true);
      await AdminAPI.changePassword(passwordUser.id, { newPassword: newUserPassword });
      
      setPasswordUser(null);
      setNewUserPassword('');
      setConfirmPassword('');
      
      showToast('success', 'Lösenord ändrat!');
    } catch (err) {
      showToast('error', err.message || 'Kunde inte ändra lösenord');
      console.error('Failed to change password:', err);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (user.username === 'chairman') {
      showToast('error', 'Kan inte ta bort ordförandekontot');
      return;
    }

    if (user.id === currentUser?.id) {
      showToast('error', 'Kan inte ta bort dig själv');
      return;
    }

    const confirmed = window.confirm(`Är du säker på att du vill ta bort användaren "${user.username}"?`);
    if (!confirmed) return;

    try {
      await AdminAPI.deleteUser(user.id);
      await loadUsers();
      showToast('success', 'Användare borttagen!');
    } catch (err) {
      showToast('error', err.message || 'Kunde inte ta bort användare');
      console.error('Failed to delete user:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Aldrig';
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return 'Aldrig';
      return d.toLocaleString('sv-SE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Aldrig';
    }
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleDateString('sv-SE');
    } catch {
      return '-';
    }
  };

  return (
    <div style={{ padding: '2rem' }}>
      {/* Toast Notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '1rem 1.5rem',
          borderRadius: '4px',
          color: '#fff',
          fontWeight: 500,
          zIndex: 1000,
          background: toast.type === 'success' ? '#5C7A3E' : '#B03030',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h2 style={{ 
            fontFamily: '"Playfair Display", serif',
            fontSize: '1.8rem',
            color: '#2C4A2E',
            margin: 0
          }}>
            Användare
          </h2>
          <p style={{ color: '#7A8070', margin: '0.5rem 0 0' }}>
            Hantera användare och behörigheter
          </p>
        </div>
      </div>

      {/* Create User Button */}
      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={() => isAdmin && setShowCreateForm(!showCreateForm)}
          disabled={!isAdmin}
          title={isAdmin ? '' : 'Kräver administratörsbehörighet'}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '4px',
            background: isAdmin ? '#2C4A2E' : '#9CA3AF',
            color: '#fff',
            fontSize: '0.9rem',
            fontWeight: 700,
            cursor: isAdmin ? 'pointer' : 'not-allowed',
            opacity: isAdmin ? 1 : 0.6
          }}
        >
          {showCreateForm ? 'Avbryt' : '+ Skapa ny användare'}
        </button>
      </div>

      {/* Create User Form */}
      {showCreateForm && isAdmin && (
        <div style={{
          background: '#F5F0E8',
          border: '1px solid #D8D0C0',
          borderRadius: '6px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ 
            fontFamily: '"Playfair Display", serif',
            fontSize: '1.2rem',
            color: '#2C4A2E',
            margin: '0 0 1.5rem'
          }}>
            Skapa ny användare
          </h3>
          <form onSubmit={handleCreateUser}>
            <div style={{ display: 'grid', gap: '1rem', maxWidth: '400px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  marginBottom: '0.5rem'
                }}>
                  Användarnamn *
                </label>
                <input
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="t.ex. monica"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: "'Lato', sans-serif"
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  marginBottom: '0.5rem'
                }}>
                  Lösenord *
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: "'Lato', sans-serif"
                  }}
                />
                <p style={{
                  fontSize: '0.75rem',
                  color: '#666',
                  marginTop: '0.5rem',
                  marginBottom: 0
                }}>
                  Minst 8 tecken, innehålla stor och liten bokstav samt specialtecken (!@#$%^&* etc.)
                </p>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  marginBottom: '0.5rem'
                }}>
                  Roll *
                </label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: "'Lato', sans-serif",
                    background: '#fff'
                  }}
                >
                  {roleOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button
                  type="submit"
                  disabled={isCreating}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '4px',
                    background: '#2C4A2E',
                    color: '#fff',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    cursor: isCreating ? 'not-allowed' : 'pointer',
                    opacity: isCreating ? 0.7 : 1
                  }}
                >
                  {isCreating ? 'Skapar...' : 'Skapa användare'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    background: '#fff',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  Avbryt
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Users Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#7A8070' }}>
          Laddar användare...
        </div>
      ) : (
        <div style={{
          background: '#fff',
          border: '1px solid #D8D0C0',
          borderRadius: '6px',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F5F0E8' }}>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'left',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  borderBottom: '1px solid #D8D0C0'
                }}>
                  Användarnamn
                </th>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'left',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  borderBottom: '1px solid #D8D0C0'
                }}>
                  Roll
                </th>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'left',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  borderBottom: '1px solid #D8D0C0'
                }}>
                  Skapad
                </th>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'left',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  borderBottom: '1px solid #D8D0C0'
                }}>
                  Senast inloggad
                </th>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'right',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  borderBottom: '1px solid #D8D0C0'
                }}>
                  Åtgärder
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} style={{ borderBottom: '1px solid #E8E0D0' }}>
                  <td style={{ padding: '1rem', fontWeight: 500 }}>
                    {user.username}
                    {user.username === 'chairman' && (
                      <span style={{ 
                        marginLeft: '0.5rem', 
                        fontSize: '0.7rem', 
                        color: '#2C4A2E',
                        background: '#E8F5E9',
                        padding: '0.1rem 0.4rem',
                        borderRadius: '3px'
                      }}>
                        Ordförande
                      </span>
                    )}
                    {user.id === currentUser?.id && (
                      <span style={{ 
                        marginLeft: '0.5rem', 
                        fontSize: '0.7rem', 
                        color: '#7A8070'
                      }}>
                        (du)
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {roleLabels[user.role] || user.role}
                  </td>
                  <td style={{ padding: '1rem', color: '#7A8070', fontSize: '0.9rem' }}>
                    {formatShortDate(user.createdAt)}
                  </td>
                  <td style={{ padding: '1rem', color: '#7A8070', fontSize: '0.9rem' }}>
                    {formatDate(user.lastLogin)}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'right' }}>
                    <button
                      onClick={() => {
                        if (isAdmin) {
                          setEditingUser(user);
                          setEditRole(user.role);
                        }
                      }}
                      disabled={!isAdmin}
                      title={isAdmin ? '' : 'Kräver administratörsbehörighet'}
                      style={{
                        padding: '0.4rem 0.8rem',
                        marginRight: '0.5rem',
                        border: '1px solid #D8D0C0',
                        borderRadius: '4px',
                        background: '#fff',
                        fontSize: '0.8rem',
                        cursor: isAdmin ? 'pointer' : 'not-allowed',
                        opacity: isAdmin ? 1 : 0.5
                      }}
                    >
                      Redigera
                    </button>
                    <button
                      onClick={() => {
                        if (isAdmin) {
                          setPasswordUser(user);
                          setNewUserPassword('');
                          setConfirmPassword('');
                        }
                      }}
                      disabled={!isAdmin}
                      title={isAdmin ? '' : 'Kräver administratörsbehörighet'}
                      style={{
                        padding: '0.4rem 0.8rem',
                        marginRight: '0.5rem',
                        border: '1px solid #D8D0C0',
                        borderRadius: '4px',
                        background: '#fff',
                        fontSize: '0.8rem',
                        cursor: isAdmin ? 'pointer' : 'not-allowed',
                        opacity: isAdmin ? 1 : 0.5
                      }}
                    >
                      Nytt lösenord
                    </button>
                    <button
                      onClick={() => isAdmin && handleDeleteUser(user)}
                      disabled={!isAdmin || user.username === 'chairman' || user.id === currentUser?.id}
                      title={
                        !isAdmin ? 'Kräver administratörsbehörighet' :
                        user.username === 'chairman' ? 'Kan inte ta bort ordförandekontot' :
                        user.id === currentUser?.id ? 'Kan inte ta bort dig själv' : ''
                      }
                      style={{
                        padding: '0.4rem 0.8rem',
                        border: '1px solid #B03030',
                        borderRadius: '4px',
                        background: '#fff',
                        color: '#B03030',
                        fontSize: '0.8rem',
                        cursor: (!isAdmin || user.username === 'chairman' || user.id === currentUser?.id) ? 'not-allowed' : 'pointer',
                        opacity: (!isAdmin || user.username === 'chairman' || user.id === currentUser?.id) ? 0.5 : 1
                      }}
                    >
                      Ta bort
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && isAdmin && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '2rem',
            width: '100%',
            maxWidth: '400px'
          }}>
            <h3 style={{ 
              fontFamily: '"Playfair Display", serif',
              fontSize: '1.3rem',
              color: '#2C4A2E',
              margin: '0 0 1.5rem'
            }}>
              Redigera användare
            </h3>
              <form onSubmit={handleUpdateUser}>
              <p style={{ marginBottom: '1rem' }}>
                <strong>Användarnamn:</strong> {editingUser.username}
              </p>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  marginBottom: '0.5rem'
                }}>
                  Roll
                </label>
                {editingUser.username === 'chairman' ? (
                  <div style={{
                    padding: '0.75rem 1rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    background: '#F5F5F5',
                    color: '#7A8070',
                    fontSize: '1rem'
                  }}>
                    Administratör (kan inte ändras)
                  </div>
                ) : (
                  <select
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: '1px solid #D8D0C0',
                      borderRadius: '4px',
                      fontSize: '1rem',
                      fontFamily: "'Lato', sans-serif",
                      background: '#fff'
                    }}
                  >
                    {roleOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="submit"
                  disabled={isSaving}
                  style={{
                    flex: 1,
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '4px',
                    background: '#2C4A2E',
                    color: '#fff',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    opacity: isSaving ? 0.7 : 1
                  }}
                >
                  {isSaving ? 'Sparar...' : 'Spara ändringar'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    background: '#fff',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  Avbryt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {passwordUser && isAdmin && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '2rem',
            width: '100%',
            maxWidth: '400px'
          }}>
            <h3 style={{ 
              fontFamily: '"Playfair Display", serif',
              fontSize: '1.3rem',
              color: '#2C4A2E',
              margin: '0 0 1.5rem'
            }}>
              Ändra lösenord
            </h3>
            <p style={{ marginBottom: '1rem' }}>
              Användare: <strong>{passwordUser.username}</strong>
            </p>
            <form onSubmit={handleChangePassword}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  marginBottom: '0.5rem'
                }}>
                  Nytt lösenord
                </label>
                <input
                  type="password"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  required
                  minLength={8}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: "'Lato', sans-serif"
                  }}
                />
                <p style={{
                  fontSize: '0.75rem',
                  color: '#666',
                  marginTop: '0.5rem',
                  marginBottom: 0
                }}>
                  Minst 8 tecken, innehålla stor och liten bokstav samt specialtecken (!@#$%^&* etc.)
                </p>
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#7A8070',
                  marginBottom: '0.5rem'
                }}>
                  Bekräfta lösenord
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: "'Lato', sans-serif"
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="submit"
                  disabled={isChangingPassword}
                  style={{
                    flex: 1,
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '4px',
                    background: '#2C4A2E',
                    color: '#fff',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    cursor: isChangingPassword ? 'not-allowed' : 'pointer',
                    opacity: isChangingPassword ? 0.7 : 1
                  }}
                >
                  {isChangingPassword ? 'Sparar...' : 'Ändra lösenord'}
                </button>
                <button
                  type="button"
                  onClick={() => setPasswordUser(null)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #D8D0C0',
                    borderRadius: '4px',
                    background: '#fff',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  Avbryt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
